import { getHostname } from "@/scripts/getHostname";
import { signOut } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { auth, storage } from "../../firebaseConfig";
import { endpoints } from "../../hooks/endpoints";
import {
  getFileUrl,
  getToken,
  getUID,
  isEmailValid,
  isNameValid,
  isPhoneValid,
  textToUnicode,
} from "../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { pushModal } from "../../store/slices/modalsSlice";
import {
  invokeUserRefetch,
  userData as userDataDispatch,
} from "../../store/slices/storeSlice";
import Animation from "../Animation";
import GeneralMessageModal from "../Modals/GeneralMessageModal";
import Spinner from "../Spinner";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";

interface UserI {
  display_name: string;
  profile_pic: string;
  user_email: string;
  city: string;
  school: string;
  user_login: string;
  father_phone: string;
  mother_phone: string;
}

const AccountPage = ({
  dataNotCompleted = false,
}: {
  dataNotCompleted?: boolean;
}) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [userData, setUserData] = useState<UserI>({
    display_name: "",
    profile_pic: "",
    user_email: "",
    city: "",
    school: "",
    user_login: "",
    father_phone: "",
    mother_phone: "",
  });
  const [isPending, setIsPending] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const oldPassRef = useRef<HTMLInputElement>();
  const newPassRef = useRef<HTMLInputElement>();

  const userInfo = useAppSelector((state) => state.store.userProfileData);
  const storeData = useAppSelector((state) => state.store);

  useEffect(() => {
    if (userInfo) setUserData(userInfo);
  }, [userInfo]);

  const handlePasswordChange = (e) => {
    e.preventDefault();
    setIsPending(true);
    fetch(endpoints.changePassword, {
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      method: "POST",
      body: JSON.stringify({
        current_password: oldPassRef?.current?.value ?? "",
        new_password: newPassRef?.current?.value ?? "",
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        toast.success("تم تغيير كلمة المرور بنجاح");
        setIsPending(false);
      })
      .catch((err) => {
        toast.error("خطأ في تغيير كلمة المرور");
        console.log("change_password", err);
        setIsPending(false);
      });
  };

  const handleDataChange = async () => {
    const checkName = isNameValid(userData.display_name);
    const fullName = document.getElementById("fullName");
    if (!checkName.isValid) {
      fullName.style = "border: 1px solid red";
      toast.error(checkName.message, {
        duration: 0,
      });
      return;
    } else {
      fullName.style = "";
    }

    const checkEmail = isEmailValid(userData.user_email);
    const userEmail = document.getElementById("userEmail");
    if (!checkEmail.isValid) {
      userEmail.style = "border: 1px solid red";
      toast.error(checkEmail.message, {
        duration: 0,
      });
      return;
    } else {
      userEmail.style = "";
    }

    if (dataNotCompleted) {
      const checkFPhone = isPhoneValid(userData.father_phone);
      if (!checkFPhone.isValid) {
        const fatherPhone = document.getElementById("fatherPhone");
        fatherPhone.style = "border: 1px solid red";
        toast.error(checkFPhone.message, {
          duration: 0,
        });
        return;
      } else {
        const fatherPhone = document.getElementById("fatherPhone");
        fatherPhone.style = "";
      }
      const checkMPhone = isPhoneValid(userData.mother_phone);
      if (!checkMPhone.isValid) {
        const motherPhone = document.getElementById("motherPhone");
        motherPhone.style = "border: 1px solid red";
        toast.error(checkMPhone.message, {
          duration: 0,
        });
        return;
      } else {
        const motherPhone = document.getElementById("motherPhone");
        motherPhone.style = "";
      }
    }

    if (!userData.city) {
      const city = document.getElementById("city");
      city.style = "border: 1px solid red";
      toast.error("ادخل المدينة", {
        duration: 0,
      });
      return;
    } else {
      const city = document.getElementById("city");
      city.style = "";
    }

    if (!userData.school) {
      const school = document.getElementById("school");
      school.style = "border: 1px solid red";
      toast.error("ادخل المدرسة", {
        duration: 0,
      });
      return;
    } else {
      const school = document.getElementById("school");
      school.style = "";
    }

    setIsPending(true);
    fetch(endpoints.updateUserInfo, {
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      method: "POST",
      body: JSON.stringify(
        // {
        //   ...userData,
        //   profile_pic:
        //     userData.profile_pic ??
        //     "https://eliteacademymedia.s3.us-east-1.amazonaws.com/placeholder-profile-image-2.png",
        //   dataNotCompleted,
        // }
        {
          name: userData.display_name,
          email: userData.user_email,
          profile_pic:
            userData.profile_pic ??
            "https://eliteacademymedia.s3.us-east-1.amazonaws.com/placeholder-profile-image-2.png",
          city: userData.city,
          school: userData.school,
          father_phone: userData.father_phone,
          mother_phone: userData.mother_phone,
        }
      ),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success !== true) {
          toast.error(data.message || "حدث خطأ");
          setIsPending(false);
          return;
        }
        setIsPending(false);
        toast.success("تم تغيير البيانات بنجاح");
        dispatch(invokeUserRefetch());
      })
      .catch((err) => {
        console.log("change_data", err);
        toast.error("حدث خطأ");
        setIsPending(false);
      });
  };

  const handleUploadProfilePicFb = async (e) => {
    if (!e.target.files[0]) {
      dispatch(
        pushModal(<GeneralMessageModal msg="يجب اختيار صورة" btnMsg="حسنا" />)
      );
      return;
    }
    setImageLoading(true);
    try {
      const imageRef = ref(storage, `profile_pics-${getUID()}`);
      await uploadBytes(imageRef, e.target.files[0]);
      const url = await getDownloadURL(imageRef);
      setUserData((prev) => ({
        ...prev,
        profile_pic: getFileUrl(url),
      }));
    } catch {
      dispatch(
        pushModal(
          <GeneralMessageModal msg="حدث خطأ أثناء رفع الصورة" btnMsg="حسنا" />
        )
      );
    }
    setImageLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    dispatch(userDataDispatch(null));
    signOut(auth);
    navigate("/");
  };

  if (!userData) return <Spinner />;

  return (
    <Animation expand>
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 pb-8 mt-12">
          <div className="container grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile & Edit */}
            <div
              className={`${
                dataNotCompleted ? "lg:col-span-3" : "lg:col-span-2"
              } space-y-6`}
            >
              {dataNotCompleted && (
                <div className="py-2 px-3 rounded-lg bg-red-100 text-red-600 border border-red-400 text-lg">
                  يرجى إكمال البيانات
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="font-bold text-2xl">
                    بيانات الحساب
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-6">
                    <div className="relative">
                      <img
                        src={
                          userData.profile_pic
                            ? decodeURIComponent(userData.profile_pic)
                            : "https://eliteacademymedia.s3.us-east-1.amazonaws.com/placeholder-profile-image-2.png"
                        }
                        alt="Profile"
                        className={`w-20 h-20 rounded-full object-cover border-2 border-gray-300 ${
                          imageLoading && "loading"
                        }`}
                      />
                      <input
                        type="file"
                        className="hidden"
                        id="uploadPic"
                        onChange={handleUploadProfilePicFb}
                      />
                      <Button
                        size="sm"
                        className="absolute -bottom-2 -right-2 rounded-full bg-primary"
                        onClick={() =>
                          document.getElementById("uploadPic").click()
                        }
                      >
                        تغيير
                      </Button>
                    </div>
                    <div>
                      <h3 className="font-semibold">{userData.display_name}</h3>
                      {!dataNotCompleted && (
                        <p className="text-gray-600">{userData.user_login}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                    <div>
                      <label htmlFor="fullName">الإسم</label>
                      <input
                        id="fullName"
                        className="p-2 my-2 border-1 rounded-md w-full  "
                        value={userData.display_name}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            display_name: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="userEmail">البريد الإلكتروني</label>
                      <input
                        disabled
                        id="userEmail"
                        className="p-2 my-2 border-1 rounded-md w-full  "
                        value={userData.user_email}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            user_email: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="city">المحافظة</label>
                      <input
                        id="city"
                        className="p-2 my-2 border-1 rounded-md w-full  "
                        value={userData.city}
                        onChange={(e) =>
                          setUserData({ ...userData, city: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="school">المدرسة</label>
                      <input
                        id="school"
                        className="p-2 my-2 border-1 rounded-md w-full  "
                        value={userData.school}
                        onChange={(e) =>
                          setUserData({ ...userData, school: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>رقم الأب</label>
                      <input
                        id="fatherPhone"
                        className="p-2 my-2 border-1 rounded-md w-full  "
                        value={userData.father_phone}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            father_phone: e.target.value,
                          })
                        }
                        contentEditable={false}
                        disabled={!dataNotCompleted}
                      />
                    </div>
                    <div>
                      <label>رقم الأم</label>
                      <input
                        id="motherPhone"
                        className="p-2 my-2 border-1 rounded-md w-full  "
                        value={userData.mother_phone}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            mother_phone: e.target.value,
                          })
                        }
                        contentEditable={false}
                        disabled={!dataNotCompleted}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleDataChange}
                    disabled={isPending}
                    className="min-w-1/4"
                  >
                    {isPending ? <Spinner variant="button" /> : "حفظ البيانات"}
                  </Button>
                </CardContent>
              </Card>

              {!dataNotCompleted && (
                <Card>
                  <CardHeader>
                    <CardTitle className="font-bold">تغيير كلمة السر</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <label>كلمة السر الحالية</label>
                      <Input ref={oldPassRef} type="password" required />
                      <label>كلمة السر الجديدة</label>
                      <Input ref={newPassRef} type="password" required />
                      <Button
                        type="submit"
                        disabled={isPending}
                        className="min-w-1/4"
                      >
                        {isPending ? (
                          <Spinner variant="button" />
                        ) : (
                          "تغيير كلمة السر"
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {!dataNotCompleted && (
                <Button variant="destructive" onClick={handleLogout}>
                  تسجيل الخروج
                </Button>
              )}
            </div>

            {/* QR & Stats */}
            {!dataNotCompleted && (
              <div>
                <Card>
                  <CardHeader className="w-full">
                    <CardTitle className="font-bold w-full text-center">
                      رمز الطالب
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center w-full flex items-center justify-center">
                    <QRCode
                      value={JSON.stringify({
                        name: textToUnicode(userData.display_name),
                        uid: getUID(),
                        portal: getHostname(),
                      })}
                      size={180}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* {isPending && } */}
        </div>
      </div>
    </Animation>
  );
};

export default AccountPage;
